'use client';

import { createContext, ReactNode, useContext, useState } from "react";
import { ConfirmDialog } from "./ConfirmDialog";

interface ConfirmOptions {
    title?: string;
    description?: string;
}

interface ConfirmContextType {
    confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export function ConfirmProvider({ children }: { children: ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState<ConfirmOptions>({});
    const [resolver, setResolver] = useState<((value: boolean) => void) | null>(null);

    function confirm(options: ConfirmOptions = {}) {
        setOptions(options);
        setIsOpen(true);
        return new Promise<boolean>((resolve) => {
            setResolver(() => resolve);
        });
    }

    function handleConfirm() {
        setIsOpen(false);
        resolver?.(true);
    }

    function handleCancel() {
        setIsOpen(false);
        resolver?.(false);
    }

    return (
        <ConfirmContext.Provider value={{confirm}}>
            {children}
            <ConfirmDialog 
                isOpen={isOpen}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                title={options.title}
                description={options.description}
            />
        </ConfirmContext.Provider>
    );
}

export function useConfirm() {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error("useConfirm must be used within a ConfirmProvider");
    }
    return context.confirm;
}